import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { EnvironmentService } from '../shared/environment.service';
import { ClientLoggingService } from '../shared/client-logging.service';
import {
  NormalizedPoint,
  PageRegionAttachment,
  PageRegionShapeName,
  PageRegionSourceStatus,
  RawImageAttachment,
  RawPageRegionSource,
  toChatAttachment,
  toPageRegionSource,
} from '../models/chat.models';
import { roundPolygon } from '../shared/page-region/region-geometry';

/** What a caller supplies to crop part of a plan page. */
export interface PageRegionCropRequest {
  projectId: string;
  fileId: string;
  fileName?: string;
  pageNumber: number;
  shape: PageRegionShapeName;
  /** Polygon in unrotated page-normalized coordinates. */
  polygon: NormalizedPoint[];
  /** Desired long edge of the image in pixels; the server clamps it. */
  maxLongEdgePixels?: number;
  description?: string;
}

/** A crop request that failed, with a message written for the user. */
export class PageRegionCropError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'PageRegionCropError';
  }
}

/**
 * Requests server-rendered crops of plan pages (Issue #1747).
 *
 * This is the reusable entry point for "give me an image of this part of this page". The browser
 * sends only geometry; the backend authorizes the project, renders the region from the stored page
 * PDF, and stages the result as an image attachment the caller owns. Nothing here reads canvas
 * pixels, so search highlights, measurement marks and toolbars can never end up in the image.
 *
 * Every created crop is also published on `regionCaptured$`, so a workflow other than chat can react
 * to regions captured on Preview without being wired to the chat component.
 */
@Injectable({ providedIn: 'root' })
export class PageRegionCropService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);

  private readonly apiBase = `https://${this.env.getApiServer()}/v1/chat/attachments`;

  private captured = new Subject<PageRegionAttachment>();
  /** Emits every region crop created through this service. */
  readonly regionCaptured$ = this.captured.asObservable();

  /** Renders and stages a crop, returning the typed attachment. */
  async createCrop(request: PageRegionCropRequest): Promise<PageRegionAttachment> {
    const body = {
      projectId: request.projectId,
      fileId: request.fileId,
      fileName: request.fileName ?? '',
      pageNumber: request.pageNumber,
      shape: request.shape === 'rectangle' ? 'PAGE_REGION_RECTANGLE' : 'PAGE_REGION_LASSO',
      polygon: roundPolygon(request.polygon),
      maxLongEdgePixels: request.maxLongEdgePixels ?? 0,
      description: request.description ?? '',
    };
    let raw: RawImageAttachment;
    try {
      raw = await firstValueFrom(this.http.post<RawImageAttachment>(`${this.apiBase}:fromPageRegion`, body));
    } catch (error) {
      throw this.toCropError(error, 'The image of this area could not be prepared. Please try again.');
    }
    const attachment = toChatAttachment(raw);
    if (!attachment.pageRegion || attachment.state !== 'ready') {
      throw new PageRegionCropError('The image of this area could not be prepared. Please try again.', 500);
    }
    const result = attachment as PageRegionAttachment;
    this.captured.next(result);
    return result;
  }

  /** Reports whether the page a region attachment came from has changed since capture. */
  async getSourceStatus(attachmentId: string): Promise<PageRegionSourceStatus> {
    try {
      const raw = await firstValueFrom(
        this.http.get<{
          attachmentId?: string;
          source?: RawPageRegionSource;
          stale?: boolean;
          pageAvailable?: boolean;
          message?: string;
        }>(`${this.apiBase}/${encodeURIComponent(attachmentId)}/page-region-status`)
      );
      return {
        attachmentId: raw.attachmentId ?? attachmentId,
        source: toPageRegionSource(raw.source) ?? null,
        stale: !!raw.stale,
        pageAvailable: !!raw.pageAvailable,
        message: raw.message || undefined,
      };
    } catch (error) {
      throw this.toCropError(error, 'The source of this image could not be checked.');
    }
  }

  /**
   * Deletes a crop that was previewed but never attached. Failures are logged only: an unsent draft
   * is reclaimed by the server's retention sweep regardless.
   */
  async discard(attachmentId: string): Promise<void> {
    try {
      await firstValueFrom(this.http.delete(`${this.apiBase}/${encodeURIComponent(attachmentId)}`));
    } catch {
      ClientLoggingService.log('WARN', '[PageRegionCrop] Could not release an unused region crop');
    }
  }

  private toCropError(error: unknown, fallback: string): PageRegionCropError {
    if (error instanceof HttpErrorResponse) {
      const message = (error.error as { message?: string } | undefined)?.message;
      if (error.status === 0) {
        return new PageRegionCropError('The image could not be prepared. Check your connection and try again.', 0);
      }
      return new PageRegionCropError(message || fallback, error.status);
    }
    return new PageRegionCropError(fallback, 500);
  }
}
